// Copyright (c) 2023 embyt GmbH. All rights reserved.
// Author: Roman Morawek/Marc Planeck

import * as React from "react";
import { connect } from "react-redux";
import Select, {
  OnChangeValue,
  ActionMeta,
  SelectComponentsConfig,
  MultiValue,
  SingleValue,
  components,
  DropdownIndicatorProps,
  NoticeProps,
} from "react-select";
import { defineMessages, WrappedComponentProps, injectIntl, FormattedMessage } from "react-intl";
import { MessageDescriptor } from "react-intl";

import { IState } from "states/states";
import { IAction } from "actions";
import { isSet } from "utils";
import cssContent from "content.scss";

export type IDropdownSelectOption = {
  index: number;
  id: number;
  source?: "input" | "output";
  // value is needed for the react-Select component -> value is used to
  // evaluate if an entry is selected or not - it needs to be unique inside the list!
  value: string;
  // visible text of the Dropdown-Entry
  label: string;
  // is the entry visible (only relevant in combination with graph-legends)
  visible: boolean;
};

type ISelectOptions = {
  label: string | JSX.Element;
  options: IDropdownSelectOption[];
};

interface IDropdownSelectExtProps {
  isMulti?: boolean;
  components?: SelectComponentsConfig<any, any, any>;
  closeMenuOnSelect?: boolean;
  placeholder?: JSX.Element;
  onChange?: (newValue: OnChangeValue<any, any>, actionMeta: ActionMeta<any>) => void;
  allowSelectAll?: boolean;
  options: ISelectOptions[];
  value?: IDropdownSelectOption[];
  noOptionsString?: JSX.Element;
}

interface IDropdownSelectProps {}

interface IDropdownSelectState {
  inputText: string;
}

const mapStateToProps = (state: IState) => {
  const props: IDropdownSelectProps = {};

  return props;
};

interface IDropdownSelectDispatch {}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IDropdownSelectDispatch = {};
  return dispatchProps;
};

const messages = defineMessages({
  selectAll: {
    id: "Select All",
  },
});

export const SELECT_ALL_OPTION_ITEMS = -1;

export const allOption: IDropdownSelectOption = {
  value: "all-1",
  index: SELECT_ALL_OPTION_ITEMS,
  id: SELECT_ALL_OPTION_ITEMS,
  visible: true,
  label: messages.selectAll.id,
};
class DropdownSelectComponent extends React.Component<
  IDropdownSelectProps & IDropdownSelectExtProps & IDropdownSelectDispatch & WrappedComponentProps,
  IDropdownSelectState
> {
  state = {
    inputText: "",
  };

  private onChange(
    selected: MultiValue<IDropdownSelectOption> | SingleValue<IDropdownSelectOption> | null,
    event: ActionMeta<IDropdownSelectOption>,
  ) {
    const inSelected = selected as IDropdownSelectOption[];
    if (inSelected !== null && inSelected.length > 0) {
      if (event.option === allOption) {
        if (inSelected.includes(allOption)) {
          const selectedOptions: IDropdownSelectOption[] = [];
          this.props.options.forEach((optionEntry) => {
            selectedOptions.push(...optionEntry.options);
          });
          return this.props.onChange?.([event.option, ...selectedOptions], event);
        } else {
          return this.props.onChange?.([], event);
        }
      } else {
        let availableItems = 0;
        this.props.options.forEach((group) => {
          availableItems += group.options.length;
        });

        if (
          inSelected.some((item) => {
            return item.index === allOption.index;
          })
        ) {
          if (inSelected.length === 1) {
            return this.props.onChange?.([], event);
          } else if (inSelected.length === availableItems) {
            return this.props.onChange?.(
              inSelected.filter((option: IDropdownSelectOption) => {
                return option.value !== allOption.value;
              }),
              event,
            );
          }
        } else {
          if (inSelected.length === availableItems) {
            const selectedOptions: IDropdownSelectOption[] = [];
            this.props.options.forEach((optionEntry) => {
              selectedOptions.push(...optionEntry.options);
            });
            return this.props.onChange?.([allOption, ...selectedOptions], event);
          }
        }
      }
    }

    return this.props.onChange?.(selected, event);
  }

  public render() {
    const DropdownIndicator = (props: DropdownIndicatorProps<any, any>) => {
      return <div className="dropdown-toggle me-2"></div>;
    };

    const NoOptionsMessage = (props: NoticeProps) => {
      return (
        <components.NoOptionsMessage {...props}>
          <span className="custom-css-class">
            {this.props.noOptionsString ?? <FormattedMessage id="No option available" />}
          </span>
        </components.NoOptionsMessage>
      );
    };

    return (
      <Select
        isMulti={this.props.isMulti}
        closeMenuOnSelect={this.props.closeMenuOnSelect}
        placeholder={this.props.placeholder}
        value={this.props.value}
        className="dropdownselect"
        styles={{
          option: (base, { isFocused }) => ({
            ...base,
            backgroundColor: isFocused
              ? cssContent.DROPDOWN_BACKGROUND
              : cssContent.DROPDOWN_TRANSPARENT,
            ":hover": {
              backgroundColor: cssContent.DROPDOWN_BACKGROUND,
            },
          }),
          container: (provided) => ({
            ...provided,
            // top: "2px",
            // top: "-4px",
            width: "auto !important",
            minHeight: "34px",
            height: "34px",
            zIndex: 2,
          }),
          menu: (defaultStyles) => ({
            ...defaultStyles,
            marginTop: 0,
            width: "max-content",
            minWidth: "100%",
            zIndex: 10000,
          }),
          valueContainer: (css) => ({
            ...css,
            width: "auto",
          }),
          placeholder: (defaultStyles) => ({
            ...defaultStyles,
            backgroundColor: cssContent.DROPDOWN_BACKGROUND,
            color: cssContent.DROPDOWN_TEXTCOLOR,
          }),
          control: (defaultStyles) => ({
            ...defaultStyles,
            backgroundColor: cssContent.DROPDOWN_BACKGROUND,
            borderRadius: "6px",
            border: "none",
            // This line disable the blue border on focus
            boxShadow: "none",
            minHeight: "34px",
            height: "34px",
          }),
          indicatorSeparator: (defaultStyles) => ({
            ...defaultStyles,
            backgroundColor: cssContent.DROPDOWN_BACKGROUND,
            border: "none",
          }),
          dropdownIndicator: (defaultStyles) => ({
            ...defaultStyles,
            color: cssContent.DROPDOWN_TEXTCOLOR,
          }),
          menuPortal: (provided) => ({
            ...provided,
            minWidth: "auto",
          }),
          menuList: (provided) => ({
            ...provided,
            minWidth: "auto",
            height: "250px",
          }),
        }}
        components={{
          ...components,
          ...this.props.components,
          DropdownIndicator,
          NoOptionsMessage,
        }}
        controlShouldRenderValue={false}
        hideSelectedOptions={false}
        isClearable={false}
        blurInputOnSelect={false}
        options={this.translateOptions(
          this.props.allowSelectAll ? [allOption, ...this.props.options] : this.props.options,
        )}
        onChange={(selected, event) =>
          this.onChange(
            selected as MultiValue<IDropdownSelectOption>,
            event as ActionMeta<IDropdownSelectOption>,
          )
        }
        tabSelectsValue={true}
        onInputChange={(newValue, actionMeta) => {
          if (actionMeta.action === "input-change" || actionMeta.action === "menu-close") {
            this.setState({ inputText: newValue });
            return newValue;
          }
        }}
        inputValue={this.state.inputText}
      />
    );
  }

  private translateOptions(options: (IDropdownSelectOption | ISelectOptions)[]) {
    return options.map((curItem) => {
      // translate label if entry is of type MessageDescriptor
      if (isSet((curItem.label as MessageDescriptor)?.id)) {
        curItem.label = this.props.intl.formatMessage(curItem.label as MessageDescriptor);
      }
      return curItem;
    });
  }
}

const DropdownSelect = connect(mapStateToProps, mapDispatchToProps)(DropdownSelectComponent);

export default injectIntl(DropdownSelect);
